/**
 * The workspace Mates with a direct role on a folder, inside its share
 * dialog. A Mate holds a person's roles below Owner; personal Mates use their owner's
 * rights and are never offered.
 */
import { notify, useConfirmModal } from '@affine/component';
import {
  FolderApiService,
  type FolderMateRole,
  type NamedMate,
} from '@affine/core/modules/folder-api';
import { ObjectPermissionMutationService } from '@affine/core/modules/permissions';
import { PermissionRoleMenu } from '@affine/core/modules/permissions/components/permission-role-menu';
import { MATE_GRANTABLE_ROLES } from '@affine/core/modules/permissions/views/permission-editor-types';
import {
  PermissionGroupInviteSearch,
  PermissionGroupRow,
} from '@affine/core/modules/permissions/views/permission-invite-picker';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { UserFriendlyError } from '@affine/error';
import { FolderRole as FolderRoleEnum } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { AiIcon } from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  FOLDER_DIRECT_ASSIGNABLE_ROLES,
  FolderPermissionExpandableSection,
  useObjectRoleLabel,
  useObjectRoleOptions,
} from './folder-permission-core';

const FOLDER_MATE_ROLES = FOLDER_DIRECT_ASSIGNABLE_ROLES.filter(role =>
  MATE_GRANTABLE_ROLES.has(role)
);

export const FolderShareMatesSection = ({
  folderId,
  canManage,
  disabled,
}: {
  folderId: string;
  canManage: boolean;
  disabled: boolean;
}) => {
  const t = useI18n();
  const folderApiService = useService(FolderApiService);
  const objectPermissionMutation = useService(ObjectPermissionMutationService);
  const workspaceId = useService(WorkspaceService).workspace.id;
  const getRoleLabel = useObjectRoleLabel(t);
  const roleOptions = useObjectRoleOptions(FOLDER_MATE_ROLES);
  const { openConfirmModal } = useConfirmModal();
  const [mateRoles, setMateRoles] = useState<FolderMateRole[]>([]);
  const [targets, setTargets] = useState<NamedMate[]>([]);
  const [searchText, setSearchText] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [roles, grantTargets] = await Promise.all([
      folderApiService.getFolderMateRoles(workspaceId, folderId),
      canManage
        ? folderApiService.getMateGrantTargets(workspaceId)
        : Promise.resolve([]),
    ]);
    setMateRoles(roles);
    setTargets(grantTargets);
  }, [canManage, folderApiService, folderId, workspaceId]);

  useEffect(() => {
    load().catch(err =>
      notify.error({ title: UserFriendlyError.fromAny(err).message })
    );
  }, [load]);

  const change = useCallback(
    async (action: 'grant' | 'revoke', mateId: string, role?: string) => {
      setBusy(true);
      try {
        await objectPermissionMutation.execute({
          action,
          principal: { id: mateId, kind: 'mate' },
          role,
          target: { id: folderId, kind: 'folder' },
        });
        await load();
      } catch (err) {
        notify.error({ title: UserFriendlyError.fromAny(err).message });
      } finally {
        setBusy(false);
      }
    },
    [folderId, load, objectPermissionMutation]
  );

  const assignedMateIds = useMemo(
    () => new Set(mateRoles.map(grant => grant.id)),
    [mateRoles]
  );
  const locked = disabled || busy;

  if (!canManage && mateRoles.length === 0) return null;

  return (
    <FolderPermissionExpandableSection
      title={t['com.streamlain.mates.permissions.mates']()}
      defaultCollapsed
      dataTestId="folder-share-mates-section"
    >
      {canManage ? (
        <PermissionGroupInviteSearch
          compact
          title={t['com.streamlain.mates.permissions.invite-title']()}
          placeholder={t['com.streamlain.mates.permissions.search']()}
          noResultsText={t['com.streamlain.mates.permissions.no-results']()}
          roleOptions={roleOptions}
          defaultRole={FolderRoleEnum.Editor}
          groups={targets}
          assignedGroupIds={assignedMateIds}
          disabled={locked}
          searchText={searchText}
          inputTestId="folder-share-mate-search-input"
          onSearchTextChange={setSearchText}
          getGroupDescription={() =>
            t['com.streamlain.mates.permissions.mate']()
          }
          onSelectGroup={(mate, role) =>
            change('grant', mate.id, role ?? FolderRoleEnum.Editor)
          }
        />
      ) : null}
      {mateRoles.map(grant => (
        <PermissionGroupRow
          key={grant.id}
          dataTestId={`folder-share-mate-row-${grant.id}`}
          groupName={grant.name}
          description={t['com.streamlain.mates.permissions.mate']()}
          icon={<AiIcon />}
          roleSlot={
            canManage ? (
              <PermissionRoleMenu
                role={grant.role}
                label={getRoleLabel(grant.role)}
                options={roleOptions}
                onSelect={role => void change('grant', grant.id, role)}
                disabled={locked}
                triggerKind="menu-trigger"
                testId={`folder-share-mate-role-${grant.id}`}
              />
            ) : (
              <span>{getRoleLabel(grant.role)}</span>
            )
          }
          onRemove={
            canManage
              ? () =>
                  openConfirmModal({
                    title: t['com.streamlain.mates.permissions.remove-title'](),
                    description: t[
                      'com.streamlain.settings.workspace.permissions.revoke-confirm-description'
                    ]({ name: grant.name }),
                    confirmText: t['Remove'](),
                    cancelText: t['Cancel'](),
                    confirmButtonOptions: { variant: 'error' },
                    onConfirm: () => change('revoke', grant.id),
                  })
              : undefined
          }
          removeDisabled={locked}
          removeTestId={`folder-share-remove-mate-${grant.id}`}
        />
      ))}
    </FolderPermissionExpandableSection>
  );
};
